/**
 * OpsDecisionsPanel — agent_decisions queue surface for the founder.
 *
 * Lives at: AdminDashboardPage → "Agent Decisions" tab.
 * Backend endpoints:
 *   GET  /admin/ops/decisions                — list pending decisions
 *   POST /admin/ops/decisions/{id}/decide    — record the founder's choice
 *
 * Each row in agent_decisions is an agent saying "I can't decide this
 * alone — here are the options I considered, here's what I'd lean
 * toward, here's why." The founder reads, picks an option (or rejects),
 * adds an optional note, and the row gets marked decided. The agent's
 * downstream effect (approve/reject the vetting, escalate the dispute,
 * etc.) happens via the existing handler path the agent already runs.
 *
 * Phase 2.2 ships only the VIEW + RESOLVE actions. Triggering the
 * downstream agent re-run on resolve is a Tier-3 follow-up — for now
 * the founder will manually flip vet_profiles.decision after picking
 * 'approve_anyway' here (or use the SQL one-liner).
 */
import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Loader2, RefreshCw, Check, X, AlertCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import axios from "axios";
import { API } from "@/App";
import { getAdminToken } from "@/lib/authStorage";

const STAKES_BADGES = {
  low:      { className: "bg-blue-100 text-blue-900 border-blue-200" },
  medium:   { className: "bg-amber-100 text-amber-900 border-amber-200" },
  high:     { className: "bg-orange-100 text-orange-900 border-orange-200" },
  critical: { className: "bg-red-100 text-red-900 border-red-200" },
};

const URGENCY_BADGES = STAKES_BADGES;   // same color palette

export default function OpsDecisionsPanel() {
  const [decisions, setDecisions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [showResolved, setShowResolved] = useState(false);
  const [resolveDialog, setResolveDialog] = useState({ open: false, decision: null, choice: null, note: "" });
  const [submitting, setSubmitting] = useState(false);

  const headers = () => ({ Authorization: `Bearer ${getAdminToken()}` });

  async function fetchDecisions() {
    setRefreshing(true);
    try {
      const r = await axios.get(`${API}/admin/ops/decisions`, {
        headers: headers(),
        params: { include_decided: showResolved, limit: 100 },
      });
      setDecisions(r.data?.decisions || []);
    } catch (e) {
      console.error("Fetch ops/decisions failed", e);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }

  useEffect(() => {
    fetchDecisions();
    /* eslint-disable react-hooks/exhaustive-deps */
  }, [showResolved]);

  async function submitResolution() {
    if (!resolveDialog.decision || !resolveDialog.choice) return;
    setSubmitting(true);
    try {
      await axios.post(
        `${API}/admin/ops/decisions/${resolveDialog.decision.id}/decide`,
        {
          founder_choice: resolveDialog.choice,
          outcome:        "resolved",
          note:           resolveDialog.note || null,
        },
        { headers: headers() },
      );
      setResolveDialog({ open: false, decision: null, choice: null, note: "" });
      await fetchDecisions();
    } catch (e) {
      console.error("Resolve failed", e);
      alert(`Resolve failed: ${e.response?.data?.detail || e.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="w-8 h-8 animate-spin text-slateline-400" />
      </div>
    );
  }

  const pending  = decisions.filter(d => !d.decided_at);
  const resolved = decisions.filter(d => d.decided_at);

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="space-y-6">
      {/* Header strip */}
      <div className="bg-canvas-card border border-slateline-200 rounded-sm p-4 flex items-center justify-between">
        <div>
          <h2 className="font-display text-xl">Agent Decisions Queue</h2>
          <p className="text-sm text-slateline-500 mt-1">
            Items where an agent escalated to you. {pending.length} pending
            {showResolved ? `, ${resolved.length} resolved` : ""}.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input
              type="checkbox"
              checked={showResolved}
              onChange={(e) => setShowResolved(e.target.checked)}
              className="rounded border-slateline-300"
            />
            Show resolved
          </label>
          <Button variant="outline" size="sm" onClick={fetchDecisions} disabled={refreshing}>
            {refreshing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </div>

      {/* Empty state */}
      {decisions.length === 0 && (
        <div className="bg-canvas-card border border-slateline-200 rounded-sm p-12 text-center">
          <Check className="w-12 h-12 text-brand-green mx-auto mb-3" />
          <h3 className="font-display text-lg mb-1">All clear</h3>
          <p className="text-slateline-500">No agent decisions awaiting your review.</p>
        </div>
      )}

      {/* Decision cards */}
      <div className="space-y-3">
        {decisions.map((d) => {
          const isResolved = !!d.decided_at;
          const ctx = d.context || {};
          const opts = Array.isArray(d.options) ? d.options : [];

          return (
            <div
              key={d.id}
              className={`bg-canvas-card border rounded-sm p-5 ${
                isResolved ? "border-slateline-200 opacity-70" : "border-slateline-300"
              }`}
            >
              {/* Top row — agent + badges + timestamp */}
              <div className="flex items-start justify-between gap-4 mb-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <code className="text-sm font-mono bg-slateline-100 px-2 py-0.5 rounded">
                    {d.agent_id}
                  </code>
                  <span className="text-sm text-slateline-500">·</span>
                  <span className="text-sm text-slateline-500">{d.pod}</span>
                  {d.stakes && (
                    <Badge variant="outline" className={STAKES_BADGES[d.stakes]?.className}>
                      stakes: {d.stakes}
                    </Badge>
                  )}
                  {d.urgency && (
                    <Badge variant="outline" className={URGENCY_BADGES[d.urgency]?.className}>
                      <Clock className="w-3 h-3 mr-1" />
                      {d.urgency}
                    </Badge>
                  )}
                  {isResolved && (
                    <Badge variant="outline" className="bg-green-100 text-green-900 border-green-200">
                      <Check className="w-3 h-3 mr-1" />
                      resolved: {d.founder_choice || d.outcome}
                    </Badge>
                  )}
                </div>
                <div className="text-xs text-slateline-500 whitespace-nowrap">
                  {new Date(d.surfaced_at).toLocaleString()}
                </div>
              </div>

              {/* Category + key context fields */}
              {ctx.category && (
                <div className="text-sm text-slateline-700 mb-2">
                  <strong>Category:</strong> {ctx.category}
                </div>
              )}
              {ctx.vet_profile_id && (
                <div className="text-xs text-slateline-500 mb-2 font-mono">
                  vet_profile_id: {ctx.vet_profile_id} · expert_id: {ctx.expert_id} ·
                  score: {ctx.vet_score} · confidence: {ctx.vet_confidence}
                </div>
              )}
              {/* P2: layer-4 calibration evidence, written by the admin score route */}
              {ctx.calibration && (
                <div className="text-xs text-slateline-700 mb-2">
                  <strong>Calibration (L4):</strong> {ctx.calibration.total}/100 on{" "}
                  <code className="font-mono">{ctx.calibration.seed}</code>{" "}
                  ({ctx.calibration.passed ? "passed" : "below"} pass mark {ctx.calibration.pass_mark})
                  {ctx.calibration.gate && (
                    <> · gate: {ctx.calibration.gate.would_approve
                      ? "allows approval"
                      : `pending_human (${ctx.calibration.gate.reason})`}</>
                  )}
                </div>
              )}

              {/* Rationale */}
              {d.rationale && (
                <div className="bg-slateline-100/40 border-l-2 border-slateline-300 px-3 py-2 mb-3 text-sm">
                  <div className="text-xs uppercase tracking-wider text-slateline-500 mb-1">
                    Agent rationale
                  </div>
                  {d.rationale}
                </div>
              )}

              {/* Options */}
              {opts.length > 0 && (
                <div className="space-y-1 mb-3">
                  <div className="text-xs uppercase tracking-wider text-slateline-500">
                    Options
                  </div>
                  {opts.map((opt, i) => (
                    <div
                      key={i}
                      className={`flex items-center justify-between px-3 py-2 rounded-sm border ${
                        opt.recommendation === d.recommendation
                          ? "border-brand-green bg-brand-green/5"
                          : "border-slateline-200"
                      }`}
                    >
                      <div className="flex items-center gap-2">
                        <code className="text-sm font-mono">{opt.recommendation}</code>
                        {opt.recommendation === d.recommendation && (
                          <Badge variant="outline" className="bg-brand-green/10 text-brand-green border-brand-green/30 text-xs">
                            agent's pick
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs text-slateline-500">
                        confidence: {Math.round((opt.confidence || 0) * 100)}%
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Resolve buttons (only pending rows) */}
              {!isResolved && opts.length > 0 && (
                <div className="flex flex-wrap gap-2 pt-3 border-t border-slateline-200">
                  {opts.map((opt) => (
                    <Button
                      key={opt.recommendation}
                      size="sm"
                      variant={opt.recommendation === d.recommendation ? "default" : "outline"}
                      onClick={() =>
                        setResolveDialog({
                          open: true,
                          decision: d,
                          choice: opt.recommendation,
                          note: "",
                        })
                      }
                    >
                      Choose: {opt.recommendation}
                    </Button>
                  ))}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() =>
                      setResolveDialog({ open: true, decision: d, choice: "defer", note: "" })
                    }
                  >
                    Defer
                  </Button>
                </div>
              )}

              {/* Resolved details */}
              {isResolved && (
                <div className="pt-3 border-t border-slateline-200 text-sm text-slateline-600">
                  <div>
                    <strong>Founder choice:</strong> {d.founder_choice}
                  </div>
                  <div>
                    <strong>Outcome:</strong> {d.outcome}
                  </div>
                  {d.decided_at && (
                    <div className="text-xs text-slateline-500 mt-1">
                      Resolved {new Date(d.decided_at).toLocaleString()}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Resolve dialog */}
      <Dialog
        open={resolveDialog.open}
        onOpenChange={(open) => !open && setResolveDialog({ open: false, decision: null, choice: null, note: "" })}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Resolve agent decision</DialogTitle>
          </DialogHeader>
          {resolveDialog.decision && (
            <div className="space-y-3">
              <div className="text-sm text-slateline-600">
                Agent: <code className="font-mono">{resolveDialog.decision.agent_id}</code>
              </div>
              <div className="text-sm">
                <strong>Your choice:</strong>{" "}
                <code className="font-mono bg-slateline-100 px-2 py-0.5 rounded">
                  {resolveDialog.choice}
                </code>
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">Note (optional)</label>
                <Textarea
                  value={resolveDialog.note}
                  onChange={(e) => setResolveDialog((s) => ({ ...s, note: e.target.value }))}
                  placeholder="Why did you make this call? Goes into the audit trail."
                  rows={3}
                />
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-sm p-3 text-xs text-amber-900 flex items-start gap-2">
                <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  This marks the decision row as resolved with your choice. For
                  vetting decisions, you may need to also update the
                  expert's vet_profile manually (the auto-apply path is a
                  Tier-3 follow-up).
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setResolveDialog({ open: false, decision: null, choice: null, note: "" })}
            >
              Cancel
            </Button>
            <Button onClick={submitResolution} disabled={submitting}>
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </motion.div>
  );
}
